import React, { useState, useEffect, useMemo, useCallback } from 'react';
import client from './api/client';
import PageHeader from './components/PageHeader';
import CharacterSelector from './components/CharacterSelector';
import FeeSettingsInputs from './components/FeeSettingsInputs';
import Sparkline from './components/Sparkline';
import { useFeeSettings } from './hooks/useFeeSettings';
import { formatPrice, formatUnitPrice } from './utils/formatPrice';
import { investmentReturn, investmentTotals, rangePosition, returnPerDay } from './utils/investmentMath';
import './InvestmentsPage.css';

const qty = new Intl.NumberFormat();
const NEAR_TOP = 80;

const COLUMNS = [
  ['name', 'Item'],
  ['held_qty', 'Held', 'num'],
  ['avg_cost', 'Avg Cost', 'num'],
  ['jita_sell', 'Jita Sell', 'num'],
  ['net_unit', 'Net / Unit', 'num'],
  ['ret_isk', 'Return', 'num'],
  ['ret_pct', 'Return %', 'num'],
  ['range_pos', '90d Range', 'num'],
  ['history', '90d Price'],
  ['avg_age_days', 'Age', 'num'],
  ['per_day', '% / Day', 'num'],
  ['unrecorded_qty', 'Unrecorded'],
];

const sortValue = (row, key) => ({
  net_unit: row.ret?.netUnit,
  ret_isk: row.ret?.isk,
  ret_pct: row.ret?.pct,
  range_pos: row.rangePos,
  per_day: row.perDay,
}[key] ?? row[key]);

const signed = (value, format) => (
  <span className={value >= 0 ? 'text-pos' : 'text-neg'}>{value >= 0 ? '+' : ''}{format(value)}</span>
);
const formatPct = v => `${v.toFixed(1)}%`;

function RangeCell({ row }) {
  if (row.rangePos == null) return '—';
  const nearTop = row.rangePos >= NEAR_TOP;
  return (
    <span
      className="inv-range"
      title={`90-day daily average range: ${formatUnitPrice(row.range_low)} – ${formatUnitPrice(row.range_high)}`}
    >
      <span className="inv-range-track">
        <span className="inv-range-mark" style={{ left: `${Math.min(100, Math.max(0, row.rangePos))}%` }} />
      </span>
      <span className={nearTop ? 'inv-near-top' : undefined}>{Math.round(row.rangePos)}%</span>
    </span>
  );
}

function ManualCostInput({ item, onSave }) {
  const [value, setValue] = useState(item.manual_unit_cost ?? '');
  useEffect(() => setValue(item.manual_unit_cost ?? ''), [item.manual_unit_cost]);

  const save = () => {
    const next = value === '' ? null : Number(value);
    if (next !== item.manual_unit_cost) onSave(item.type_id, next);
  };

  return (
    <input
      type="number" min="0" step="0.01" className="inv-cost-input"
      placeholder="Unit cost" value={value}
      title="Unit cost for units no Jita or Perimeter corp purchase covers. Leave blank to exclude them."
      onChange={e => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
    />
  );
}

function InvestmentsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const [sort, setSort] = useState({ key: 'ret_pct', desc: true });
  const fees = useFeeSettings({ autoSave: true });

  const load = useCallback(async () => {
    try {
      const res = await client.get('/investments/');
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load investments.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    setRefreshMessage(null);
    try {
      const res = await client.post('/investments/refresh/');
      setRefreshMessage(res.data.messages.join(' · '));
      await load();
    } catch (err) {
      setRefreshMessage(err.response?.data?.error || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const saveCost = async (typeId, unitCost) => {
    try {
      await client.post('/investments/cost-basis/', { type_id: typeId, unit_cost: unitCost });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save cost.');
    }
  };

  const rows = useMemo(() => {
    const withReturns = (data?.items || []).map(item => {
      const ret = investmentReturn(item, fees.sellRevenueMultiplier);
      return { ...item, ret, rangePos: rangePosition(item), perDay: returnPerDay(item, ret) };
    });
    const dir = sort.desc ? -1 : 1;
    return withReturns.sort((a, b) => {
      const av = sortValue(a, sort.key), bv = sortValue(b, sort.key);
      if (av == null) return 1;
      if (bv == null) return -1;
      return (typeof av === 'string' ? av.localeCompare(bv) : av - bv) * dir;
    });
  }, [data, fees.sellRevenueMultiplier, sort]);

  const totals = useMemo(() => investmentTotals(rows), [rows]);
  const unrecorded = rows.filter(r => r.unrecorded_qty > 0 && r.manual_unit_cost == null).length;
  const nearTop = rows.filter(r => r.rangePos >= NEAR_TOP).length;

  const toggleSort = key => setSort(s => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }));
  const fetched = (data?.characters || []).map(c =>
    `${c.character_name}: ${c.assets_fetched_at ? new Date(c.assets_fetched_at).toLocaleString() : 'never fetched'}`);

  return (
    <div className="page">
      <PageHeader
        title="Investments"
        meta={['Jita 4-4 hangars', ...fetched]}
      >
        <button className="btn btn-primary" onClick={refresh} disabled={refreshing || !data?.characters.length}>
          {refreshing ? 'Fetching…' : 'Fetch'}
        </button>
      </PageHeader>
      <div className="page-content">
        {!data && !error && <div className="page-status">Loading investments...</div>}
        {error && <div className="page-status error">{error}</div>}
        {refreshMessage && <div className="notice">{refreshMessage}</div>}
        {data?.warnings.map(w => <div key={w} className="notice notice-warn">{w}</div>)}
        <CharacterSelector trackingType="investments" onCharactersUpdated={load} />

        {data && (
          <>
            <div className="kpis">
              <div className="kpi"><span>Cost Basis</span><b>{formatPrice(totals.cost)}</b></div>
              <div className="kpi">
                <span>Value if Listed</span><b>{formatPrice(totals.value)}</b>
                <small>Jita lowest sell, after fees</small>
              </div>
              <div className="kpi">
                <span>Return</span><b>{signed(totals.isk, formatPrice)}</b>
                {nearTop > 0 && <small>{nearTop} items in the top {100 - NEAR_TOP}% of their 90d range</small>}
              </div>
              <div className="kpi">
                <span>Return %</span><b>{totals.pct == null ? '—' : signed(totals.pct, formatPct)}</b>
                {unrecorded > 0 && <small>{unrecorded} items have unrecorded units</small>}
              </div>
            </div>

            <section className="panel">
              <div className="panel-head"><h2>Fees</h2></div>
              <div className="panel-body settings-row">
                <FeeSettingsInputs {...fees} />
              </div>
            </section>

            <section className="panel">
              <div className="table-scroll">
                <table className="data-table inv-table">
                  <thead>
                    <tr>
                      {COLUMNS.map(([key, label, cls]) => (key === 'history' ? <th key={key}>{label}</th> : (
                        <th key={key} className={`sortable ${cls || ''}`} onClick={() => toggleSort(key)}>
                          {label}{sort.key === key ? (sort.desc ? ' ▼' : ' ▲') : ''}
                        </th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={COLUMNS.length} className="table-empty">
                        {data.characters.length
                          ? 'Nothing in the selected characters\' Jita 4-4 hangars. Fetch to load their assets.'
                          : 'Select the characters whose Jita 4-4 hangars hold your investments.'}
                      </td></tr>
                    )}
                    {rows.map(row => (
                      <tr key={row.type_id}>
                        <td>{row.name}</td>
                        <td className="num" title={`${qty.format(row.hangar_qty)} in hangar, ${qty.format(row.listed_qty)} listed`}>
                          {qty.format(row.held_qty)}
                          {row.listed_qty > 0 && <small className="text-faint"> ({qty.format(row.listed_qty)} listed)</small>}
                        </td>
                        <td className="num">{row.avg_cost == null ? '—' : formatUnitPrice(row.avg_cost)}</td>
                        <td className="num">{formatUnitPrice(row.jita_sell)}</td>
                        <td className="num">{row.ret ? formatUnitPrice(row.ret.netUnit) : '—'}</td>
                        <td className="num">{row.ret ? signed(row.ret.isk, formatPrice) : '—'}</td>
                        <td className="num">{row.ret?.pct != null ? signed(row.ret.pct, formatPct) : '—'}</td>
                        <td className="num"><RangeCell row={row} /></td>
                        <td>
                          <Sparkline
                            points={row.history} reference={row.avg_cost} current={row.jita_sell}
                            label={`${row.name}: 90-day Forge daily average, ${formatUnitPrice(row.range_low)} to ${formatUnitPrice(row.range_high)}`}
                          />
                        </td>
                        <td className="num" title={row.oldest_buy ? `Oldest held lot bought ${new Date(row.oldest_buy).toLocaleDateString()}` : undefined}>
                          {row.avg_age_days == null ? '—' : `${Math.round(row.avg_age_days)}d`}
                        </td>
                        <td className="num">{row.perDay == null ? '—' : signed(row.perDay, v => `${v.toFixed(2)}%`)}</td>
                        <td>
                          {row.unrecorded_qty > 0 && (
                            <span className="inv-unrecorded">
                              <span className={row.manual_unit_cost == null ? 'text-warn' : 'text-dim'}>
                                {qty.format(row.unrecorded_qty)}
                              </span>
                              <ManualCostInput item={row} onSave={saveCost} />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default InvestmentsPage;
